import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { simulatedOrdersApi, scheduledOrdersApi, clientsApi } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarDays,
  ClipboardCheck,
  Users,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowRight,
  TrendingUp,
  Package,
  RefreshCw,
  Loader2,
  XCircle,
} from "lucide-react";
import { format, differenceInDays, isAfter, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const SIMULATED_FILTERS_STORAGE_KEY = "simulated_dashboard_filters";

export default function SimulatedOrdersDashboard() {
  const [ searchTerm, setSearchTerm ] = useState( () => {
    const saved = localStorage.getItem( SIMULATED_FILTERS_STORAGE_KEY );
    if ( saved ) {
      try {
        return JSON.parse( saved ).searchTerm || "";
      } catch ( e ) { return ""; }
    }
    return "";
  } );
  const [ selectedSimFilter, setSelectedSimFilter ] = useState<string>( () => {
    const saved = localStorage.getItem( SIMULATED_FILTERS_STORAGE_KEY );
    if ( saved ) {
      try {
        return JSON.parse( saved ).selectedSimFilter || "all";
      } catch ( e ) { return "all"; }
    }
    return "all";
  } );
  const [ adherenceFilter, setAdherenceFilter ] = useState<string>( () => {
    const saved = localStorage.getItem( SIMULATED_FILTERS_STORAGE_KEY );
    if ( saved ) {
      try {
        return JSON.parse( saved ).adherenceFilter || "all";
      } catch ( e ) { return "all"; }
    }
    return "all";
  } );
  const [ selectedStateFilter, setSelectedStateFilter ] = useState<string>( () => {
    const saved = localStorage.getItem( SIMULATED_FILTERS_STORAGE_KEY );
    if ( saved ) {
      try {
        return JSON.parse( saved ).selectedStateFilter || "all";
      } catch ( e ) { return "all"; }
    }
    return "all";
  } );

  useEffect( () => {
    localStorage.setItem( SIMULATED_FILTERS_STORAGE_KEY, JSON.stringify( {
      searchTerm,
      selectedSimFilter,
      adherenceFilter,
      selectedStateFilter
    } ) );
  }, [ searchTerm, selectedSimFilter, adherenceFilter, selectedStateFilter ] );

  const queryClient = useQueryClient();

  const { data: simulatedOrders, isLoading: isLoadingSimulated } = useQuery( {
    queryKey: [ "simulated-orders" ],
    queryFn: () => simulatedOrdersApi.getAll(),
  } );

  const { data: scheduledOrders, isLoading: isLoadingScheduled } = useQuery( {
    queryKey: [ "scheduled-orders" ],
    queryFn: () => scheduledOrdersApi.getAll(),
  } );

  const { data: clients, isLoading: isLoadingClients } = useQuery( {
    queryKey: [ "clients" ],
    queryFn: () => clientsApi.getAll(),
  } );

  const isLoading = isLoadingSimulated || isLoadingScheduled || isLoadingClients;

  // Extrair estados únicos dos clientes
  const availableStates = useMemo( () => {
    if ( !clients?.data ) return [];
    const states = clients.data
      .map( ( c: any ) => c.clientState )
      .filter( ( s: string ) => !!s );
    return Array.from( new Set( states ) ).sort() as string[];
  }, [ clients ] );

  // Função utilitária para verificar se um pedido pertence a um simulado específico
  const isOrderFromSim = ( order: any, simDef: any ) => {
    if ( !order.orderNumber.startsWith( 'PED-SIM' ) ) return false;

    let payload = order.orderPayload;
    if ( typeof payload === 'string' ) {
      try { payload = JSON.parse( payload ); } catch ( e ) { return false; }
    }

    const items = payload?.Items || payload?.items || payload?.item || payload?.Item || [];
    if ( !Array.isArray( items ) ) return false;

    // Normalize products array from simulation definition
    let simProducts = simDef.products;
    if ( typeof simProducts === 'string' ) {
      try { simProducts = JSON.parse( simProducts ); } catch ( e ) { simProducts = []; }
    }
    if ( !Array.isArray( simProducts ) ) simProducts = [];

    return items.some( ( item: any ) => {
      const itemProductId = item.ProductId || item.productId || item.product_id;
      const itemProductCode = item.ProductCode || item.productCode || item.codigo_produto;

      return simProducts.some( ( p: any ) =>
        ( itemProductId && p.id === itemProductId ) ||
        ( itemProductCode && ( p.codigo_produto === itemProductCode || p.codigoProduto === itemProductCode ) )
      );
    } );
  };

  const stats = useMemo( () => {
    if ( !simulatedOrders?.data || !scheduledOrders?.data || !clients?.data ) return null;

    const definitions = simulatedOrders.data;
    const orders = scheduledOrders.data.filter( o => o.orderNumber.startsWith( 'PED-SIM' ) );
    const totalClients = clients.data.length;

    const sortedDeadlines = [ ...definitions ].sort( ( a, b ) =>
      parseISO( a.deadline ).getTime() - parseISO( b.deadline ).getTime()
    );
    const nextSimulated = sortedDeadlines.find( d => isAfter( parseISO( d.deadline ), new Date() ) );

    const totalOrdersCount = orders.length;
    const pendingOrdersCount = orders.filter( o => o.status === 'pending' || o.status === 'processing' ).length;
    const processedOrdersCount = orders.filter( o => o.status === 'processed' || o.status === 'completed' ).length;
    const failedOrdersCount = orders.filter( o => o.status === 'failed' ).length;
    const adherenceRate = ( totalOrdersCount / ( totalClients * definitions.length ) ) * 100;

    return {
      nextSimulated,
      totalOrdersCount,
      pendingOrdersCount,
      processedOrdersCount,
      failedOrdersCount,
      adherenceRate,
      totalDefinitions: definitions.length
    };
  }, [ simulatedOrders, scheduledOrders, clients ] );

  const simulationsData = useMemo( () => {
    if ( !simulatedOrders?.data || !scheduledOrders?.data ) return [];

    const now = new Date();

    // 1. Ordenar as definições por prazo para estabelecer as janelas temporais
    const sortedDefs = [ ...simulatedOrders.data ].sort( ( a, b ) =>
      parseISO( a.deadline ).getTime() - parseISO( b.deadline ).getTime()
    );

    // 2. Mapear cada definição calculando seus pedidos específicos
    const mapped = sortedDefs.map( ( def, index ) => {
      // Janela de tempo: começa após o prazo do simulado anterior
      const prevDef = index > 0 ? sortedDefs[ index - 1 ] : null;
      const windowStart = prevDef ? parseISO( prevDef.deadline ) : new Date( 0 );
      const windowEnd = parseISO( def.deadline );

      const ordersForThisDef = scheduledOrders.data.filter( o => {
        if ( !isOrderFromSim( o, def ) ) return false;

        const orderDate = parseISO( o.createdAt );
        const isLast = index === sortedDefs.length - 1;

        // O pedido pertence a este simulado se foi feito após o simulado anterior
        // E antes (ou no dia) do prazo deste simulado (exceto se for o último configurado)
        return isAfter( orderDate, windowStart ) && ( isLast || !isAfter( orderDate, windowEnd ) );
      } );

      const pendingForThisDef = ordersForThisDef.filter( o => o.status === 'pending' || o.status === 'processing' );
      const processedForThisDef = ordersForThisDef.filter( o => o.status === 'processed' || o.status === 'completed' );
      const failedForThisDef = ordersForThisDef.filter( o => o.status === 'failed' );

      const daysRemaining = differenceInDays( parseISO( def.deadline ), now );

      return {
        ...def,
        ordersCount: ordersForThisDef.length,
        pendingOrdersCount: pendingForThisDef.length,
        processedOrdersCount: processedForThisDef.length,
        failedOrdersCount: failedForThisDef.length,
        daysRemaining,
        isExpired: daysRemaining < 0
      };
    } );

    const nextSimIndex = mapped.findIndex( s => !s.isExpired );

    return mapped.map( ( s, index ) => ( {
      ...s,
      isCurrent: index === nextSimIndex
    } ) );
  }, [ simulatedOrders, scheduledOrders ] );

  const filteredClients = useMemo( () => {
    if ( !clients?.data || !scheduledOrders?.data ) return [];

    const clientsWithStats = clients.data.map( client => {
      const clientOrders = scheduledOrders.data.filter( o =>
        o.clientCode === client.clientCodeCnx && o.orderNumber.startsWith( 'PED-SIM' )
      );

      let orderedSelectedSim = false;
      if ( selectedSimFilter !== "all" && simulatedOrders?.data ) {
        const targetSim = simulatedOrders.data.find( s => s.id.toString() === selectedSimFilter );
        if ( targetSim ) {
          orderedSelectedSim = clientOrders.some( o => isOrderFromSim( o, targetSim ) );
        }
      }

      return {
        ...client,
        ordersCount: clientOrders.length,
        lastOrder: clientOrders[ 0 ],
        orderedSelectedSim
      };
    } );

    return clientsWithStats.filter( client => {
      const nomeFantasia = ( client.clientFantasyName || "" ).toLowerCase();
      const razaoSocial = ( client.clientCompanyName || "" ).toLowerCase();
      const codigo = ( client.clientCodeCnx || "" ).toLowerCase();
      const term = searchTerm.toLowerCase();
      const matchesSearch = nomeFantasia.includes( term ) || razaoSocial.includes( term ) || codigo.includes( term );

      if ( !matchesSearch ) return false;

      // 1. Filtro por Estado (UF)
      if ( selectedStateFilter !== "all" && client.clientState !== selectedStateFilter ) return false;

      // 2. Se um simulado específico estiver selecionado, o filtro de adesão DEVE respeitar aquele simulado
      if ( selectedSimFilter !== "all" ) {
        if ( adherenceFilter === "ordered" && !client.orderedSelectedSim ) return false;
        if ( adherenceFilter === "not_ordered" && client.orderedSelectedSim ) return false;
      } else {
        // Se nenhuma campanha específica, usa a adesão global
        if ( adherenceFilter === "ordered" && client.ordersCount === 0 ) return false;
        if ( adherenceFilter === "not_ordered" && client.ordersCount > 0 ) return false;
      }

      return true;
    } );
  }, [ clients, scheduledOrders, searchTerm, selectedSimFilter, adherenceFilter, selectedStateFilter, simulatedOrders ] );

  const { mutateAsync: sendOrdersBatch, isPending: isProcessingBatch } = useMutation( {
    mutationFn: ( size: number ) => scheduledOrdersApi.sendOrders( size ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: [ "scheduled-orders" ] } );
      toast.success( "Processamento em lote iniciado com sucesso!" );
    },
    onError: () => {
      toast.error( "Erro ao iniciar processamento em lote." );
    }
  } );

  const handleProcessAll = async () => {
    if ( stats && stats.pendingOrdersCount > 0 ) {
      await sendOrdersBatch( scheduledOrders?.data.length || 100 );
    }
  };

  if ( isLoading ) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Monitoramento de Simulados</h1>
          <p className="text-muted-foreground">Acompanhamento de prazos e adesão das escolas aos Simulados ENEM e Avaliações Nacionais.</p>
        </div>
        {/* <div className="flex gap-2">
           <Button variant="outline" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Relatório Logístico
          </Button>
          <Button 
            variant="default" 
            className="gap-2 bg-primary" 
            onClick={handleProcessAll}
            disabled={!stats?.pendingOrdersCount || isProcessingBatch}
          >
            {isProcessingBatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Processar Lote Pendente
          </Button>
        </div> */}
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-primary shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Próximo Simulado</CardTitle>
            <CalendarDays className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-lg font-bold truncate">
              {stats?.nextSimulated?.name || "Nenhum agendado"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Prazo: {stats?.nextSimulated ? format( parseISO( stats.nextSimulated.deadline ), "dd/MM/yyyy" ) : "-"}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-slate-400 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Total Pedidos</CardTitle>
            <ClipboardCheck className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-2xl font-bold">{stats?.totalOrdersCount}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Simulados via Portal B2B</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-info shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Aguardando Proc.</CardTitle>
            <Clock className="h-4 w-4 text-info" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-2xl font-bold text-info">{stats?.pendingOrdersCount}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Pendente de processamento</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Processados</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-2xl font-bold text-success">{stats?.processedOrdersCount}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Integrado com sucesso</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-destructive shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Falhas</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-2xl font-bold text-destructive">{stats?.failedOrdersCount}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Pedidos com erro de envio</p>
          </CardContent>
        </Card>

        {/* <Card className="border-l-4 border-l-warning shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase">Adesão Geral</CardTitle>
            <Users className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent className="pb-4">
            <div className="text-2xl font-bold">{stats?.adherenceRate.toFixed( 1 )}%</div>
            <Progress value={stats?.adherenceRate} className="h-1.5 mt-2" />
          </CardContent>
        </Card> */}
      </div>

      <Tabs defaultValue="simulations" className="space-y-6">
        <TabsList className="bg-muted p-1">
          <TabsTrigger value="simulations" className="gap-2">
            <Clock className="h-4 w-4" />
            Prazos por Simulado
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-2">
            <Users className="h-4 w-4" />
            Adesão por Escola
          </TabsTrigger>
        </TabsList>

        <TabsContent value="simulations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {simulationsData.map( ( sim ) => (
              <Card key={sim.id} className={cn(
                "overflow-hidden transition-all",
                sim.isCurrent ? "ring-2 ring-primary shadow-lg scale-[1.02]" : "hover:shadow-md"
              )}>
                <CardHeader className={cn(
                  "pb-3",
                  sim.isCurrent ? "bg-primary text-primary-foreground" : sim.isExpired ? "bg-muted/30" : "bg-primary/5"
                )}>
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-lg">{sim.name}</CardTitle>
                      {sim.isCurrent && (
                        <Badge variant="secondary" className="w-fit bg-primary-foreground text-primary hover:bg-primary-foreground text-[10px] py-0">
                          CAMPANHA ATUAL
                        </Badge>
                      )}
                    </div>
                    <Badge variant={sim.isExpired ? "destructive" : sim.isCurrent ? "secondary" : "outline"} className={cn(
                      sim.isCurrent && "bg-primary-foreground text-primary hover:bg-primary-foreground border-none"
                    )}>
                      {sim.isExpired ? "Encerrado" : `${ sim.daysRemaining } dias restantes`}
                    </Badge>
                  </div>
                  <CardDescription className={cn(
                    sim.isCurrent ? "text-primary-foreground/80" : "text-muted-foreground"
                  )}>
                    Prazo final: {format( parseISO( sim.deadline ), "dd 'de' MMMM", { locale: ptBR } )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Total</p>
                      <p className="text-xl font-bold text-foreground">{sim.ordersCount}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Pendente</p>
                      <p className={cn(
                        "text-xl font-bold",
                        sim.pendingOrdersCount > 0 ? "text-info" : "text-foreground"
                      )}>{sim.pendingOrdersCount}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Processado</p>
                      <p className={cn(
                        "text-xl font-bold",
                        sim.processedOrdersCount > 0 ? "text-success" : "text-foreground"
                      )}>{sim.processedOrdersCount}</p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Adesão Escolas</span>
                    <span className="font-semibold text-primary">
                      {clients?.data?.length ? ( ( sim.ordersCount / clients.data.length ) * 100 ).toFixed( 0 ) : 0}%
                    </span>
                  </div>
                  <Progress value={clients?.data?.length ? ( sim.ordersCount / clients.data.length ) * 100 : 0} className="h-2" />

                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground uppercase font-bold mb-2">Produtos Inclusos</p>
                    <div className="flex flex-wrap gap-1">
                      {sim.products.slice( 0, 3 ).map( ( p: any ) => (
                        <Badge key={p.id} variant="outline" className="text-[10px] bg-background">
                          {p.sku || p.codigo_produto}
                        </Badge>
                      ) )}
                      {sim.products.length > 3 && (
                        <Badge variant="outline" className="text-[10px] bg-background">
                          +{sim.products.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) )}
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <Card>
            <CardHeader>
              <div className="flex flex-col space-y-4">
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                  <div>
                    <CardTitle>Listagem de Escolas</CardTitle>
                    <CardDescription>Verifique quais escolas já realizaram seus pedidos de simulados.</CardDescription>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 bg-muted/20 p-4 rounded-lg border border-muted">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-muted-foreground px-1">Busca Rápida</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Escola ou código..."
                        value={searchTerm}
                        onChange={( e ) => setSearchTerm( e.target.value )}
                        className="pl-9 bg-background"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-muted-foreground px-1">Filtro por Simulado</label>
                    <Select value={selectedSimFilter} onValueChange={setSelectedSimFilter}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Selecione a Campanha" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Campanhas</SelectItem>
                        {simulatedOrders?.data?.map( sim => (
                          <SelectItem key={sim.id} value={sim.id.toString()}>{sim.name}</SelectItem>
                        ) )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-muted-foreground px-1">Estado (UF)</label>
                    <Select value={selectedStateFilter} onValueChange={setSelectedStateFilter}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Filtrar por UF" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Estados</SelectItem>
                        {availableStates.map( uf => (
                          <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                        ) )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-muted-foreground px-1">Adesão à Campanha</label>
                    <Select value={adherenceFilter} onValueChange={setAdherenceFilter}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Filtrar por adesão" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Escolas</SelectItem>
                        <SelectItem value="ordered">Pedido Realizado de Contrato</SelectItem>
                        <SelectItem value="not_ordered">Pedido Não Realizado de Contrato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end pb-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSimFilter( "all" );
                        setAdherenceFilter( "all" );
                        setSelectedStateFilter( "all" );
                        setSearchTerm( "" );
                      }}
                      className="text-muted-foreground hover:text-primary h-9 gap-2"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Escola</TableHead>
                      <TableHead>Código CNX</TableHead>
                      <TableHead>Cidade/UF</TableHead>
                      <TableHead className="text-center">Adesão ao Simulado</TableHead>
                      <TableHead>Último Pedido</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.length > 0 ? (
                      filteredClients.map( ( client ) => (
                        <TableRow key={client.clientId || client.id} className="hover:bg-slate-50/50 transition-colors">
                          <TableCell>
                            <div className="font-medium text-slate-900">{client.clientFantasyName || client.clientCompanyName}</div>
                            <div className="text-xs text-slate-500">{client.clientEmail || 'Sem e-mail cadastrado'}</div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{client.clientCodeCnx}</TableCell>
                          <TableCell className="text-sm">
                            {client.clientCity}/{client.clientState}
                          </TableCell>
                          <TableCell className="text-center">
                            {selectedSimFilter !== "all" ? (
                              <div className="flex flex-col items-center gap-1">
                                {client.orderedSelectedSim ? (
                                  <Badge variant="secondary" className="bg-success/10 text-success hover:bg-success/10 border-success/20 gap-1.5">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Realizado de Contrato
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-destructive border-destructive/20 bg-destructive/5 gap-1.5">
                                    <XCircle className="h-3 w-3" />
                                    Pendente
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <span className="font-bold">{client.ordersCount} / {simulatedOrders?.data?.length || 0}</span>
                                <Progress
                                  value={( client.ordersCount / ( simulatedOrders?.data?.length || 1 ) ) * 100}
                                  className="w-16 h-1.5"
                                />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {client.lastOrder ? (
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">{client.lastOrder.orderNumber}</span>
                                <span className="text-xs text-slate-400">{format( parseISO( client.lastOrder.createdAt ), "dd/MM/yyyy" )}</span>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-slate-400 font-normal">Nenhum pedido</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <a href={`/backoffice/client/${ client.clientCodeCnx }`} className="gap-2">
                                Ver Detalhes
                                <ArrowRight className="h-4 w-4" />
                              </a>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ) )
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center text-slate-500">
                          Nenhuma escola encontrada com os filtros atuais.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
